/**
 * EditClassificationModal (S1.4, extended S2.1) — chunked edit for 分類與識別:
 * subsidiary, 5-capable category (TaxonomyCascader — hidden levels excluded
 * from new-selection only), external identifiers, Car Details (DD!31 variant /
 * DD!32 extract year), DD!69 FA Sub Category, and the C108 vehicle year range.
 * The Age Group itself is SYSTEM-COMPUTED (pure `computeAgeGroup`) and renders
 * as a read-only chip — the year inputs are the only editable surface.
 * NetSuite Item ID stays read-only (Tier-1 mapping).
 *
 * R14 (Q12 Seat B — compliance-UI wave, 2026-08-31): the cascader's inline
 * create footer is wired here exactly as the wizard wires it. This closes a
 * CLICK gap, not a permission gap: `routePermissions.ts:69-70` already gives
 * `/auth/products` and `/auth/product-taxonomy` the SAME `products.manage`,
 * and `useTaxonomy.ts` `useInlineCreateNode` is the one guarded create path
 * both entries submit through. No new permission and no new copy —
 * `products:wizard.cascader.createInLevel` and `taxonomy:addRoot` already ship.
 */

import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import TaxonomyCascader from '@/components/common/TaxonomyCascader';
import CreateNodeModal from '@/components/taxonomy/CreateNodeModal';
import TableContainer from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import { linkableOemMatches, useOemDupCheck } from '../../useOemDupCheck';
import type { OemDupMatch } from '../../useOemDupCheck';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useProductMutations } from '@/hooks/useProducts';
import { useTaxonomyTree } from '@/hooks/useTaxonomy';
import { computeAgeGroup } from '@/lib/products/computeAgeGroup';
import type { Product, UpdateProductRequest } from '@/types/api/product';
import { editClassificationSchema } from '../schemas';
import type { EditClassificationFormData } from '../schemas';

const { Text } = Typography;

const SUBSIDIARY_OPTIONS = [
  { value: 'SUB-TW-HOLIEN', labelKey: 'products:wizard.subsidiaries.tw' },
  { value: 'SUB-AU-SENDIT', labelKey: 'products:wizard.subsidiaries.au' },
] as const;

// DD!69 FA Sub Category (S2.1) — aligned with the S1.1 narrative seed values;
// no reference endpoint exists yet (see followUps).
const FA_SUB_CATEGORY_VALUES = [
  'Body Plastics',
  'Brake Friction',
  'Brake Hydraulics',
  'Brake Rotor',
  'Cooling',
  'Lighting',
  'Suspension',
] as const;

interface EditClassificationModalProps {
  open: boolean;
  product: Product;
  onCancel: () => void;
  onSaved: () => void;
  /**
   * ROW 22 (F4 §2-Fix5) — 選擇式關聯 seat: the detail page wires this to its
   * ScnLinkDrawer. Hide-don't-disable: the link CTA renders only when given.
   */
  onOpenScnDrawer?: () => void;
}

/**
 * Pure PATCH payload for the 分類與識別 chunk edit — exported so the node-env
 * test proves the clear semantics against the mapper the modal actually
 * submits through (house `buildCommercialPatch` pattern; no antd render).
 *
 * D-X1 (FIELD-SIGNOFF F4 §2-Fix1, 2026-08-29): countryOfManufacture joins the
 * S2.1 string-clear doctrine — the previous `|| undefined` strip made the
 * country unclearable from BOTH entries, because the PATCH apply seat keeps
 * omitted keys. `ageGroup` is deliberately NOT sent (system-computed
 * server-side from the year range, C108).
 */
/* eslint-disable-next-line react-refresh/only-export-components -- the PATCH
   builder must live beside the modal it serves (the buildCommercialPatch
   precedent; test seat editClassificationClearSemantics.test.ts). */
export const buildClassificationPatch = (
  data: EditClassificationFormData
): UpdateProductRequest => ({
  // NO `subsidiaryId` (D-5, 2026-08-10). It used to be sent from here and the
  // field never reached the wire: `productApi.update` forwards it only on
  // CREATE, `ProductUpdateInput` declares no such field, and the server patches
  // no `subsidiary_id`. The save answered 200 and the row never moved. It is
  // create-only, so the control below is read-only and `UpdateProductRequest`
  // no longer declares it.
  includeChildren: data.includeChildren,
  categoryId: data.categoryId,
  oemNo: data.oemNo || null,
  mpn: data.mpn || null,
  upcCode: data.upcCode || null,
  // S2.1 — '' clears a stored string (D-X1: country now included).
  countryOfManufacture: data.countryOfManufacture ?? '',
  // ROW 68 (FIELD-SIGNOFF F4 §2-Fix3) — manufacturer joins its
  // Manufacturing-group neighbours in the chunk edit; apply seat already
  // accepts it (`rules/product.ts` manufacturer guard), no API delta.
  manufacturer: data.manufacturer ?? '',
  faSubCategory: data.faSubCategory ?? '',
  variant: data.variant ?? '',
  extractYear: data.extractYear ?? undefined,
  ageStartYear: data.ageStartYear ?? undefined,
  ageEndYear: data.ageEndYear ?? undefined,
});

export const EditClassificationModal = ({
  open,
  product,
  onCancel,
  onSaved,
  onOpenScnDrawer,
}: EditClassificationModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { handleError, handleSuccess } = useErrorHandler();
  const { updateProduct } = useProductMutations(product.id);
  // R14 (Q12 Seat B) — inline taxonomy create hosted from the chunk edit, same
  // shape as `ProductFormWizard`: the cascader raises `onRequestCreate(parentId)`,
  // this dialog hosts `CreateNodeModal`, and the shared guarded create path
  // (`useInlineCreateNode`) does the filing. The cached tree is the same
  // `useTaxonomyTree` instance the cascader already reads.
  const { nodes: taxonomyNodes } = useTaxonomyTree();
  const [createNodeOpen, setCreateNodeOpen] = useState(false);
  const [createNodeParentId, setCreateNodeParentId] = useState<string | null>(null);

  const formValues = useMemo<EditClassificationFormData>(
    () => ({
      subsidiaryId: product.subsidiaryId,
      includeChildren: product.includeChildren ?? false,
      categoryId: product.categoryId,
      oemNo: product.oemNo ?? '',
      mpn: product.mpn ?? '',
      upcCode: product.upcCode ?? '',
      countryOfManufacture: product.countryOfManufacture ?? '',
      manufacturer: product.manufacturer ?? '',
      faSubCategory: product.faSubCategory ?? '',
      variant: product.variant ?? '',
      extractYear: product.extractYear ?? null,
      ageStartYear: product.ageStartYear ?? null,
      ageEndYear: product.ageEndYear ?? null,
    }),
    [product]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<EditClassificationFormData>({
    resolver: zodResolver(editClassificationSchema),
    defaultValues: formValues,
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(formValues);
  }, [open, formValues, reset]);

  // C108 — live SYSTEM-COMPUTED Age Group preview over the entered year range
  // (pure `computeAgeGroup`; the chip itself is never editable).
  const [watchedAgeStartYear, watchedAgeEndYear] = useWatch({
    control,
    name: ['ageStartYear', 'ageEndYear'],
  });
  const ageGroupPreview = computeAgeGroup(
    watchedAgeStartYear,
    watchedAgeEndYear,
    new Date().getFullYear()
  );

  // ROW 22 (F4 §2-Fix5) — the SAME advisory duplicate lookup the wizard runs,
  // via the shared hook: 800ms debounce, 2-criteria key (同產品大類 +
  // 同OEM NUMBER), skip-when-unchanged-from-saved, FAILS OPEN. Display +
  // guard only: never consulted by onSubmit, and no OEM data-model change
  // (多國多OEM / UPC-QR stay owner-gated on #2438, Q3/Q4).
  const [watchedOemNo, watchedOemCategoryId, watchedOemSubsidiaryId] = useWatch({
    control,
    name: ['oemNo', 'categoryId', 'subsidiaryId'],
  });
  const { oemCheck } = useOemDupCheck({
    oemNo: watchedOemNo,
    categoryId: watchedOemCategoryId,
    subsidiaryId: watchedOemSubsidiaryId,
    savedOemNo: product.oemNo ?? '',
    excludeId: product.id,
    enabled: open,
  });
  const oemLinkable = linkableOemMatches(oemCheck.matches);
  const oemHasSameMarket = oemCheck.matches.some((match) => match.sameMarket);
  const oemHasCrossMarket = oemCheck.matches.some((match) => !match.sameMarket);

  const subsidiaryLabel = (subsidiaryId: string): string => {
    const option = SUBSIDIARY_OPTIONS.find((candidate) => candidate.value === subsidiaryId);
    return option ? t(option.labelKey) : subsidiaryId;
  };

  const handleOpenScnDrawer = () => {
    // The drawer owns linking; this dialog closes WITHOUT saving so the
    // duplicate OEM is never written on the way out (guard, never a gate).
    onCancel();
    onOpenScnDrawer?.();
  };

  const onSubmit = async (data: EditClassificationFormData) => {
    try {
      await updateProduct(buildClassificationPatch(data));
      handleSuccess(t('products:messages.updated'));
      onSaved();
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  const requiredMark = <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>;

  const textField = (
    name: 'oemNo' | 'mpn' | 'upcCode' | 'manufacturer' | 'countryOfManufacture' | 'variant',
    labelKey: string,
    suffix?: ReactNode
  ) => (
    <Col span={12}>
      <Text style={formStyles.labelStyle}>{t(labelKey)}</Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ''}
            size="large"
            suffix={suffix}
            style={formStyles.inputStyle}
          />
        )}
      />
    </Col>
  );

  const yearField = (
    name: 'extractYear' | 'ageStartYear' | 'ageEndYear',
    labelKey: string,
    span: number
  ) => (
    <Col span={span}>
      <Text style={formStyles.labelStyle}>{t(labelKey)}</Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <InputNumber
              size="large"
              min={1950}
              max={2100}
              precision={0}
              value={field.value ?? null}
              onChange={(value) => {
                field.onChange(value ?? null);
                trigger(name);
              }}
              onBlur={field.onBlur}
              status={errors[name] && touchedFields[name] ? 'error' : undefined}
              style={formStyles.inputStyle}
            />
            {errors[name] && touchedFields[name] && (
              <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
            )}
          </>
        )}
      />
    </Col>
  );

  return (
    <>
      <Modal
        title={t('products:detail.modals.editClassification')}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(onSubmit)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={isSubmitting}
        width={800}
        okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      >
        <form>
          <div style={formStyles.fieldContainerStyle}>
            <Text strong style={formStyles.sectionTitleStyle}>
              {t('products:sections.classification')}
            </Text>
            <Row gutter={[16, 16]} align="middle">
              <Col span={14}>
                <Text style={formStyles.labelStyle}>{t('products:fields.subsidiary')}</Text>
                {/*
                  CREATE-ONLY, so READ-ONLY here (D-5, 2026-08-10) — the same
                  treatment NetSuite Item ID already gets in this modal. This was
                  an editable Select whose write the wire could not carry, and
                  the save reported success over it. Rendering the value keeps the
                  context the section is for; the control is what had to go.
                */}
                <div
                  style={{
                    minHeight: `${controlHeightTokens.controlHeightXL}px`,
                    borderRadius: `${radiusTokens.card}px`,
                    border: `1px solid ${token.colorBorder}`,
                    backgroundColor: token.colorFillQuaternary,
                    display: 'flex',
                    alignItems: 'center',
                    padding: `0 ${token.paddingSM}px`,
                    color: token.colorTextSecondary,
                  }}
                >
                  {t(
                    SUBSIDIARY_OPTIONS.find((option) => option.value === product.subsidiaryId)
                      ?.labelKey ?? product.subsidiaryId
                  )}
                </div>
              </Col>
              <Col span={10}>
                <Text style={formStyles.labelStyle}>{t('products:fields.includeChildren')}</Text>
                <Controller
                  name="includeChildren"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      style={{
                        display: 'flex',
                        height: `${controlHeightTokens.controlHeightXL}px`,
                        alignItems: 'center',
                      }}
                    >
                      <Radio value={true}>{t('products:detail.yes')}</Radio>
                      <Radio value={false}>{t('products:detail.no')}</Radio>
                    </Radio.Group>
                  )}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Text style={formStyles.labelStyle}>
                  {requiredMark}
                  {t('products:fields.category')}
                </Text>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TaxonomyCascader
                        value={field.value || null}
                        onChange={(categoryId) => {
                          field.onChange(categoryId ?? '');
                          trigger('categoryId');
                        }}
                        status={errors.categoryId ? 'error' : undefined}
                        texts={{
                          placeholder: (label) =>
                            t('products:wizard.cascader.selectLevel', { label }),
                          createLabel: (label) =>
                            t('products:wizard.cascader.createInLevel', { label }),
                          nonLeafWarning: t('products:wizard.cascader.nonLeafWarning'),
                          breadcrumbLabel: t('products:wizard.cascader.breadcrumbLabel'),
                        }}
                        onRequestCreate={(parentId) => {
                          setCreateNodeParentId(parentId);
                          setCreateNodeOpen(true);
                        }}
                      />
                      {errors.categoryId && (
                        <Text style={formStyles.errorStyle}>
                          {t(errors.categoryId.message ?? '')}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>
          </div>

          <div style={formStyles.fieldContainerStyle}>
            {/* S2.1 Car Details 車型適配 — DD!31 variant, DD!32 extract year, and
              the C108 vehicle year range feeding the SYSTEM-COMPUTED Age Group
              chip (read-only — no pencil on the chip itself). */}
            <Text strong style={formStyles.sectionTitleStyle}>
              {t('products:wizard.sections.fitment')}
            </Text>
            <Row gutter={[16, 16]}>
              {textField('variant', 'products:fields.variant')}
              {yearField('extractYear', 'products:fields.extractYear', 12)}
              {yearField('ageStartYear', 'products:fields.ageStartYear', 8)}
              {yearField('ageEndYear', 'products:fields.ageEndYear', 8)}
              <Col span={8}>
                <Text style={formStyles.labelStyle}>{t('products:fields.ageGroup')}</Text>
                <div
                  style={{
                    minHeight: `${controlHeightTokens.controlHeightXL}px`,
                    borderRadius: `${radiusTokens.card}px`,
                    border: `1px solid ${token.colorBorder}`,
                    backgroundColor: token.colorFillQuaternary,
                    display: 'flex',
                    alignItems: 'center',
                    padding: `0 ${token.paddingSM}px`,
                    color: token.colorTextSecondary,
                  }}
                >
                  {ageGroupPreview ? (
                    <Tag style={{ margin: 0 }}>
                      {t(`products:ageGroupBands.${ageGroupPreview}`)}
                    </Tag>
                  ) : (
                    '-'
                  )}
                </div>
                <Text
                  type="secondary"
                  style={{ fontSize: token.fontSizeSM, display: 'block', marginTop: '4px' }}
                >
                  {t('products:wizard.ageGroupAuto')}
                </Text>
              </Col>
            </Row>
          </div>

          <div style={formStyles.fieldContainerStyle}>
            <Text strong style={formStyles.sectionTitleStyle}>
              {t('products:sections.externalIdentifiers')}
            </Text>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>{t('products:fields.netSuiteItemId')}</Text>
                <Input
                  size="large"
                  value={product.netSuiteItemId ?? ''}
                  placeholder={t('products:notAssigned')}
                  disabled
                  style={formStyles.inputStyle}
                />
              </Col>
              {/* Quiet in-flight indicator — advisory check: no blocking
                spinner, save never disabled while checking (wizard parity). */}
              {textField(
                'oemNo',
                'products:fields.oemNo',
                oemCheck.status === 'VALIDATING' ? (
                  <LoadingOutlined style={{ color: token.colorTextTertiary }} />
                ) : (
                  <span />
                )
              )}
              {textField('mpn', 'products:fields.mpn')}
              {textField('upcCode', 'products:fields.upcCode')}
              {/* ROW 68 (F4 §2-Fix3) — mirrors the ClassificationSection order:
                Manufacturer sits beside Country of Manufacture. */}
              {textField('manufacturer', 'products:fields.manufacturer')}
              {textField('countryOfManufacture', 'products:fields.countryOfManufacture')}
              <Col span={12}>
                {/* DD!69 FA Sub Category (S2.1) — manufacturing sub-classification. */}
                <Text style={formStyles.labelStyle}>{t('products:fields.faSubCategory')}</Text>
                <Controller
                  name="faSubCategory"
                  control={control}
                  render={({ field }) => (
                    <Select
                      size="large"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      value={(field.value || undefined) ?? undefined}
                      options={FA_SUB_CATEGORY_VALUES.map((value) => ({ label: value, value }))}
                      onChange={(value) => field.onChange(value ?? '')}
                      onBlur={field.onBlur}
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              </Col>
            </Row>
            {/* ROW 22 (F4 §2-Fix5) — read-only duplicate list, wizard
              presentation (same alerts / columns); linking stays the SCN
              drawer's job. The check never blocks Save (advisory). */}
            {oemCheck.status === 'MATCH' && (
              <div style={{ marginTop: '16px' }}>
                {oemHasSameMarket && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: '12px' }}
                    message={t('products:oemDup.sameMarketPrompt')}
                  />
                )}
                {oemHasCrossMarket && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: '12px' }}
                    message={t('products:oemDup.crossMarketPrompt')}
                  />
                )}
                <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                  {t('products:oemDup.matchListTitle')}
                </Text>
                <TableContainer>
                  <Table<OemDupMatch>
                    dataSource={oemCheck.matches}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: t('products:oemDup.columns.internalProductId'),
                        dataIndex: 'internalProductId',
                        key: 'internalProductId',
                      },
                      {
                        title: t('products:oemDup.columns.market'),
                        dataIndex: 'subsidiaryId',
                        key: 'market',
                        render: (value: string) => subsidiaryLabel(value),
                      },
                      {
                        title: t('products:oemDup.columns.oemNo'),
                        dataIndex: 'oemNo',
                        key: 'oemNo',
                      },
                      {
                        title: t('products:oemDup.columns.scn'),
                        dataIndex: 'scn',
                        key: 'scn',
                        render: (value: string | null) =>
                          value ? <PurpleTag>{value}</PurpleTag> : '-',
                      },
                    ]}
                  />
                </TableContainer>
                {/* Hide-don't-disable: the 選擇式關聯 CTA renders only when a
                  linkable (SCN-carrying) match is on offer AND the detail
                  page wired the drawer seat. */}
                {oemLinkable.length > 0 && onOpenScnDrawer && (
                  <div style={{ marginTop: '8px' }}>
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingLeft: 0 }}
                      onClick={handleOpenScnDrawer}
                    >
                      {t('products:oemDup.linkAction')}
                    </Button>
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                      {t('products:oemDup.blockEditDrawerHint')}
                    </Text>
                  </div>
                )}
                {oemLinkable.length === 0 && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: '12px' }}
                    message={t('products:oemDup.noLinkableNote')}
                  />
                )}
              </div>
            )}
            <Text
              type="secondary"
              style={{ fontSize: token.fontSizeSM, display: 'block', marginTop: '8px' }}
            >
              {t('products:netSuiteHelp')}
            </Text>
          </div>
        </form>
      </Modal>

      {/* R14 — inline leaf/root create for the cascader. Same host shape as the
          wizard; on success the new node becomes the selected category so the
          operator never leaves the chunk edit to file one. */}
      <CreateNodeModal
        open={createNodeOpen}
        parentId={createNodeParentId}
        nodes={taxonomyNodes}
        onClose={() => setCreateNodeOpen(false)}
        onCreated={(newNodeId) => {
          setValue('categoryId', newNodeId, { shouldDirty: true });
          trigger('categoryId');
        }}
      />
    </>
  );
};

export default EditClassificationModal;
